import * as Assets from 'models/Asset'
import { useCallback, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getAssets, getLayerAssets, getTransitionsByAssetId, selectAssetById } from 'selectors'
import {
  selectInOutPointsTimeLineAssetID,
  selectorActivePreview,
  selectShowInOutPoints
} from 'selectors/preview'
import { activeHotkeyProfileSelector } from 'selectors/user-profile-selector'
import { ClipCreatorMarkType } from 'types/preview'
import { MARK_TYPE } from 'config/constants/preview'
import { timelineTimeToSeconds } from 'Util'
import { PLAYBACK_STATE } from 'enums'
import {
  selectMediaServerSeekProcessing,
  selectMemoSliderTime,
  selectTimelineSegmentPlaybackState
} from 'selectors/playback'
import { INITIAL_MOUSE_MOVEMENT_DATA } from './constant'
import { MouseMovementDataType } from './types'

type UseInitialData = {
  currentFps: number,
  asset: Assets.MediaAsset,
  isMedia: boolean,
}

export const useInitialData = ({ currentFps, asset, isMedia }: UseInitialData) => {
  const dispatch = useDispatch()
  const mouseMovementData = useRef<MouseMovementDataType>({ ...INITIAL_MOUSE_MOVEMENT_DATA })
  const timeout = useRef<NodeJS.Timeout | null>(null)
  const ioPointsBarRef = useRef<HTMLDivElement | undefined>(undefined)

  // clip segment dragging
  const [ isDragging, setDragging ] = useState<boolean>(false)

  const activeHotkeyProfile = useSelector(activeHotkeyProfileSelector)
  const activePreview = useSelector(selectorActivePreview)
  const { timeline: memoSliderTime } = useSelector(selectMemoSliderTime)
  const timelineSegmentPlaybackState = useSelector(selectTimelineSegmentPlaybackState)

  const {
    media: showInOutPointsSourceMedia,
    timeline: showInOutPointsTimeline,
  } = useSelector(selectShowInOutPoints)
  const isMediaServerProcessing = useSelector(selectMediaServerSeekProcessing)
  const assets = useSelector(getAssets)
  const inOutPointsTimeLineAssetId = useSelector(selectInOutPointsTimeLineAssetID)
  const inOutPointsTimeLineAsset = useSelector(
    state => selectAssetById(state, inOutPointsTimeLineAssetId)
  ) as Assets.MediaAsset
  const layerAssets = useSelector(
    state => getLayerAssets(state, inOutPointsTimeLineAsset?.layerId)
  )
    .filter((asset: Assets.MediaAsset) => asset.id !== inOutPointsTimeLineAssetId)
    .sort((a: Assets.MediaAsset, b: Assets.MediaAsset) => a.startTime - b.startTime)
  const inOutPointsAssetTransitions = useSelector(state => getTransitionsByAssetId(state, inOutPointsTimeLineAssetId))
  const inOutPointsAssetTransDuration = inOutPointsAssetTransitions.reduce((sum, tr) => {
    // eslint-disable-next-line no-param-reassign
    sum += tr.duration
    return sum
  }, 0)
  const inOutPointsAssetDissolve = inOutPointsAssetTransitions.find(tr => tr.isMountBetween)
  const isStaticAsset = asset instanceof Assets.ImageAsset || asset instanceof Assets.TextAsset

  const minClipDuration = timelineTimeToSeconds(inOutPointsAssetTransDuration) || 1 / currentFps

  const getTimePointToPercent = useCallback(({ markType, duration, time }: {
    markType: ClipCreatorMarkType, duration: number, time: number
  }) => (
    markType === MARK_TYPE.IN
      ? `${((time / duration) * 100)}%`
      : `${(100 - ((time / duration) * 100))}%`
  ), [])

  const isResize = Boolean(mouseMovementData.current.markType)

  return {
    dispatch,
    mouseMovementData,
    timeout,
    activeHotkeyProfile,
    activePreview,
    showInOutPoints: isMedia ? showInOutPointsSourceMedia : showInOutPointsTimeline,
    inOutPointsTimeLineAsset,
    minClipDuration,
    getTimePointToPercent,
    layerAssets,
    inOutPointsTimeLineAssetId,
    setDragging,
    isDragging,
    ioPointsBarRef,
    isResize,
    isMediaServerProcessing,
    assets,
    isStaticAsset,
    inOutPointsAssetDissolve,
    memoSliderTime: memoSliderTime && (timelineSegmentPlaybackState !== PLAYBACK_STATE.PLAY)
      ? timelineTimeToSeconds(memoSliderTime)
      : null,
  }
}
